import { singleFlight } from './single-flight.js'

/**
 * Renovação de sessão do painel, segura entre abas (hotfix #288).
 *
 * O refresh token é ROTACIONADO a cada POST /auth/refresh. Com várias abas,
 * cada uma renovava sozinha: uma recebia 200 e a outra 401 — e o 401 apagava
 * o access token compartilhado (localStorage), derrubando TODAS as abas.
 *
 * - Na aba: single-flight (um refresh por vez).
 * - Entre abas: Web Lock exclusivo (`navigator.locks`); sem a API, só o
 *   single-flight da aba (fallback).
 * - Com o lock: relê o token; se outra aba já o trocou por um válido, usa-o
 *   SEM renovar.
 * - 401 no refresh: só limpa o token se ele ainda for o mesmo do início.
 */
export type SessionRefresherDeps = {
  getToken: () => string | null
  setToken: (token: string) => void
  clearToken: () => void
  /** token não expirado (exp no futuro) */
  isValid: (token: string) => boolean
  /** POST /auth/refresh: novo access token, ou null se a sessão acabou */
  refresh: () => Promise<string | null>
  /** Web Locks (navigator.locks) — opcional */
  locks?: { request: <T>(name: string, cb: () => Promise<T>) => Promise<T> } | undefined
  lockName?: string
}

export function createSessionRefresher(deps: SessionRefresherDeps): () => Promise<boolean> {
  const lockName = deps.lockName ?? 'sr-auth-refresh'

  async function critical(startToken: string | null): Promise<boolean> {
    const current = deps.getToken()
    // Outra aba (ou requisição) já trocou o token por um válido: usar.
    // Mesmo token: o chamador viu 401/expiração — precisa renovar.
    if (current && current !== startToken && deps.isValid(current)) return true
    let fresh: string | null
    try {
      fresh = await deps.refresh()
    } catch {
      return false // erro de rede: não derruba a sessão das outras abas
    }
    if (fresh) {
      deps.setToken(fresh)
      return true
    }
    // Outra aba pode ter renovado enquanto este refresh voava.
    const after = deps.getToken()
    if (after && after !== startToken && deps.isValid(after)) return true
    if (after === startToken) deps.clearToken()
    return false
  }

  return singleFlight(async () => {
    const startToken = deps.getToken()
    if (deps.locks) {
      try {
        return await deps.locks.request(lockName, () => critical(startToken))
      } catch {
        /* Web Locks indisponível/negado: segue sem o lock entre abas */
      }
    }
    return critical(startToken)
  })
}

/** `exp` do JWT (sem verificar assinatura) ainda no futuro, com folga. */
export function jwtNotExpired(token: string, nowSec = Math.floor(Date.now() / 1000), skewSec = 5) {
  try {
    const part = token.split('.')[1] ?? ''
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: number }).exp
    return typeof exp === 'number' && exp > nowSec + skewSec
  } catch {
    return false
  }
}

/**
 * Resolve a sessão existente para a tela de login (#291): SEMPRE termina —
 * true se o refresh deu certo, false em falha, erro (rejeição) ou se passar
 * de `timeoutMs` (rede pendurada). Garante que o loading inicial do /login
 * nunca fica preso.
 */
export function resolveSessionWithin(
  refresh: () => Promise<boolean>,
  timeoutMs = 10_000,
): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs)
    let p: Promise<boolean>
    try {
      p = refresh()
    } catch {
      p = Promise.resolve(false)
    }
    p.then(
      (ok) => resolve(ok === true),
      () => resolve(false),
    ).finally(() => clearTimeout(timer))
  })
}
